import { StringUtil } from '@ddt-web/generic-utils';

import { CommonDataUtil, PieDataUtil } from '../../data-mapper';
import { IHighChartsAttributesModel } from '../../models/high-charts-attributes.interface';
import { BaseChartWorker } from '../common';

/**
 * PieChartWorker: chart worker specific to a Pie Chart.
 * @param {any} config JSON object representing the chart configuration.
 * @param {IHighChartsAttributesModel} highChartsAttributes an object containing properties used to augment the chart configuration.
 */
export class PieChartWorker extends BaseChartWorker {
	constructor(config: any, highChartsAttributes: IHighChartsAttributesModel) {
		super(config, highChartsAttributes);
		if (this.highChartsAttributes.isPdf) {
			this.initChartForPdf();
		} else {
			this.initChartForWeb();
		}
	}

	private initChartForWeb(): void {
		// TODO: Abstract and create responsive rules dispatcher
		const donutMaxWidth = 200;
		const containerWidth = this.highChartsAttributes.container.nativeElement.offsetWidth;
		const chartHeight = this.highChartsAttributes.height ? this.highChartsAttributes.height : containerWidth <= donutMaxWidth ? containerWidth : donutMaxWidth;
		const chartWidth = this.highChartsAttributes.width ? this.highChartsAttributes.width : containerWidth;
		this.config.chart.height = chartHeight;
		this.config.chart.width = chartWidth;
		if (this.config.responsive && this.config.responsive.rules) {
			this.config.responsive.rules[1].chartOptions.chart.height = chartHeight + 90;
			this.config.responsive.rules[0].chartOptions.chart.height = chartHeight + 60;
		}
		const titleCalculatedWidth = Math.min(containerWidth || donutMaxWidth, donutMaxWidth, this.highChartsAttributes.width || donutMaxWidth);
		this.config.title.widthAdjust = titleCalculatedWidth >= 175 ? titleCalculatedWidth - containerWidth - 100 : titleCalculatedWidth - containerWidth - 50;
		this.config.title.style.fontSize = titleCalculatedWidth >= 200 ? '14px' : '11px';
		this.config.title.style.lineHeight = titleCalculatedWidth >= 200 ? '18px' : '13px';
		this.config.tooltip.enabled = 'useTooltip' in this.highChartsAttributes ? this.highChartsAttributes.useTooltip : true;

		if (this.highChartsAttributes?.customAttributes) {
			this.applyCustomAttributes();
		}
	}

	private initChartForPdf(): void { }

	public transformData(): void {
		this.config.colors = this.config.colors || CommonDataUtil.getDefaultColorsForTabData(this.highChartsAttributes.tabData);
		this.config.series = PieDataUtil.getDefaultPieSeriesForTabData(
			this.highChartsAttributes.tabData, this.highChartsAttributes.portfolioIndex, 0, this.highChartsAttributes.ignoreEmpty
		);
		if (this.config.series[0] && this.config.colors && this.config.series[0].data.length > this.config.colors.length) {
			this.config.colors.push('#ccc');
		}

		// format the title text when title is in middle of the pie chart
		let chartTitle: string = PieDataUtil.getDefaultPieTitleForTabData(this.highChartsAttributes.tabData, this.highChartsAttributes.portfolioIndex);
		if (chartTitle) {
			const parts = StringUtil.splitStringByPattern(chartTitle, 14, 50);
			chartTitle = StringUtil.joinStringPartsForChartTitle(parts, 14, 4);
		}
		this.config.title.text = this.highChartsAttributes.showTitle ? chartTitle : null;
	}

	private applyCustomAttributes(): void {
		if ('isHoverEnabled' in this.highChartsAttributes.customAttributes) {
			this.config.plotOptions.pie.states = {
				hover: {
					enabled: this.highChartsAttributes.customAttributes.isHoverEnabled
				},
				inactive: {
					enabled: this.highChartsAttributes.customAttributes.isHoverEnabled
				},
				normal: {
					enabled: this.highChartsAttributes.customAttributes.isHoverEnabled
				},
				select: {
					enabled: this.highChartsAttributes.customAttributes.isHoverEnabled
				}
			};
		}

		if ('drawAnimationEnabled' in this.highChartsAttributes.customAttributes) {
			this.config.plotOptions.pie.animation = this.highChartsAttributes.customAttributes.drawAnimationEnabled;
		}

		if (this.highChartsAttributes.customAttributes.responsiveInnerSize) {
			this.config.responsive.rules[0].chartOptions.plotOptions.pie.innerSize = this.highChartsAttributes.customAttributes.responsiveInnerSize;
		}

		if (this.highChartsAttributes.customAttributes.responsiveTitleY) {
			this.config.responsive.rules[0].chartOptions.title.y = this.highChartsAttributes.customAttributes.responsiveTitleY;
		}
	}
}
